// src/constants/enums/transferStatus.js

/**
 * =====================================================
 * ASSET TRANSFER STATUS GOVERNANCE
 * =====================================================
 *
 * PURPOSE:
 * - Legacy status field backward compatibility
 * - UI display labels and colors
 * - Ledger-safe filtering helpers
 *
 * IMPORTANT ARCHITECTURE NOTE:
 * ─────────────────────────────────────────────────────
 * TRANSFER_STATUS is the LEGACY status field.
 * It is kept for backward compatibility with existing
 * Firestore documents that have a `status` field.
 *
 * NEW workflow governance uses:
 *   workflow_state  → WORKFLOW_STATES
 *   workflow_type   → WORKFLOW_TYPES
 *   transaction_class → TRANSACTION_CLASSES
 *   visibility_bucket → WORKFLOW_BUCKETS
 *
 * DO NOT use TRANSFER_STATUS for any new workflow logic.
 * DO NOT add new states here.
 * All new state checks must use WORKFLOW_STATES.
 * ─────────────────────────────────────────────────────
 *
 * USED BY:
 * - assetTransactionService (legacy status field only)
 * - assetReplayService (legacy doc compatibility)
 * - assetLedgerService (legacy doc compatibility)
 * - UI label/color rendering
 * =====================================================
 */

// =====================================================
// TRANSFER STATUS ENUM
// =====================================================

export const TRANSFER_STATUS = Object.freeze({

    /**
     * Waiting for receiver acceptance
     * Maps to: WORKFLOW_STATES.PENDING_ACCEPTANCE
     * Flows: STAFF→STAFF, SITE→STAFF, STAFF→SITE
     */
    PENDING_ACCEPTANCE: "PENDING_ACCEPTANCE",

    /**
     * Return request pending store receive
     * Maps to: WORKFLOW_STATES.RETURN_PENDING
     * Flows: STAFF→STORE, SITE→STORE
     */
    RETURN_PENDING: "RETURN_PENDING",

    /**
     * Final successful movement
     * Maps to: WORKFLOW_STATES.FINALIZED
     * Custody officially changed
     */
    COMPLETED: "COMPLETED",

    /**
     * Transfer rejected by receiver or store
     * Maps to: WORKFLOW_STATES.REJECTED
     */
    REJECTED: "REJECTED",

    /**
     * Transfer cancelled by requester
     * Maps to: WORKFLOW_STATES.CANCELLED
     */
    CANCELLED: "CANCELLED",
});

// =====================================================
// FINAL IMMUTABLE STATES
// Transactions in these states are closed
// =====================================================

export const FINAL_TRANSFER_STATUSES = Object.freeze([
    TRANSFER_STATUS.COMPLETED,
    TRANSFER_STATUS.REJECTED,
    TRANSFER_STATUS.CANCELLED,
]);

// =====================================================
// ACTIVE PENDING STATES
// Use for stale pending detection (legacy docs)
// For new docs: use ACTIVE_PENDING_WORKFLOW_STATES
// =====================================================

export const ACTIVE_PENDING_TRANSFER_STATUSES = Object.freeze([
    TRANSFER_STATUS.PENDING_ACCEPTANCE,
    TRANSFER_STATUS.RETURN_PENDING,
]);

// =====================================================
// LEGACY FALLBACK STATES
// Old Firestore documents may contain these strings
// Used only in replay/migration engine
// =====================================================

export const LEGACY_PENDING_TRANSFER_STATUSES = Object.freeze([
    "PENDING",
    "PENDING_APPROVAL",
    "PENDING_ACCEPT",
    "PENDING_STORE_APPROVAL",  // removed from active — kept for legacy replay only
    "STORE_PENDING",
    "RETURN_REQUESTED",
]);

// =====================================================
// HOLDER CHANGING STATES
// Only these statuses should trigger holder mutation
// =====================================================

export const HOLDER_CHANGING_STATUSES = Object.freeze([
    TRANSFER_STATUS.COMPLETED,
]);

// =====================================================
// VALIDATION HELPER
// =====================================================

export const isValidTransferStatus = (status) =>
    Object.values(TRANSFER_STATUS).includes(status);

export const isFinalTransferStatus = (status) =>
    FINAL_TRANSFER_STATUSES.includes(status);

export const isActivePendingTransferStatus = (status) =>
    ACTIVE_PENDING_TRANSFER_STATUSES.includes(status);

// =====================================================
// UI LABELS
// =====================================================

export const TRANSFER_STATUS_LABELS = Object.freeze({

    [TRANSFER_STATUS.PENDING_ACCEPTANCE]:
        "Pending Acceptance",

    [TRANSFER_STATUS.RETURN_PENDING]:
        "Return Pending",

    [TRANSFER_STATUS.COMPLETED]:
        "Completed",

    [TRANSFER_STATUS.REJECTED]:
        "Rejected",

    [TRANSFER_STATUS.CANCELLED]:
        "Cancelled",
});

// =====================================================
// UI COLORS
// =====================================================

export const TRANSFER_STATUS_COLORS = Object.freeze({

    [TRANSFER_STATUS.PENDING_ACCEPTANCE]:
        "info",

    [TRANSFER_STATUS.RETURN_PENDING]:
        "secondary",

    [TRANSFER_STATUS.COMPLETED]:
        "success",

    [TRANSFER_STATUS.REJECTED]:
        "error",

    [TRANSFER_STATUS.CANCELLED]:
        "default",
});